import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { 
  ArrowLeft,
  DollarSign,
  CreditCard,
  PiggyBank,
  TrendingUp,
  Calculator,
  FileText,
  ExternalLink,
  Phone,
  Users,
  Shield,
  BookOpen,
  Target,
  Wallet
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";

const FinancialResources = () => {
  const creditResources = [
    {
      title: "AnnualCreditReport.com",
      description: "Get your no-cost credit report from all 3 bureaus once per year — the only official source",
      type: "No-Cost Service",
      icon: <FileText className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "Get Free Report",
      link: "https://www.annualcreditreport.com"
    },
    {
      title: "Credit Karma",
      description: "Credit scores, monitoring, and personalized recommendations to improve your credit — at no cost",
      type: "No-Cost Service",
      icon: <TrendingUp className="w-6 h-6" />,
      color: "#87A96B",
      action: "Check Your Score",
      link: "https://www.creditkarma.com"
    },
    {
      title: "CFPB Credit Building Guide",
      description: "Consumer Financial Protection Bureau guide to understanding and building credit",
      type: "Educational Guide",
      icon: <BookOpen className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "Learn More",
      link: "https://www.consumerfinance.gov/consumer-tools/credit-reports-and-scores/"
    },
    {
      title: "myFICO Score Estimator",
      description: "Understand how different actions might affect your FICO credit score",
      type: "Interactive Tool",
      icon: <Calculator className="w-6 h-6" />,
      color: "#87A96B",
      action: "Try Estimator",
      link: "https://www.myfico.com/credit-education/improve-your-credit-score"
    }
  ];

  const budgetResources = [
    {
      title: "Rental Budget Calculator",
      description: "Our interactive tool to help you figure out how much rent you can afford",
      type: "Interactive Tool",
      icon: <Calculator className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "Open Calculator",
      internalLink: "/resources/renters/calculator"
    },
    {
      title: "Budget Worksheet",
      description: "Branded printable worksheet to plan and track your monthly budget in detail",
      type: "Branded Printable Worksheet",
      icon: <FileText className="w-6 h-6" />,
      color: "#87A96B",
      action: "Open Worksheet",
      isInteractive: true,
      internalLink: "/resources/renters/budget-worksheet"
    },
    {
      title: "CFPB Budget Planner",
      description: "Step-by-step interactive budget tool from the Consumer Financial Protection Bureau",
      type: "Interactive Tool",
      icon: <Wallet className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "Start Planning",
      link: "https://www.consumerfinance.gov/consumer-tools/spending-tracker/"
    },
    {
      title: "50/30/20 Budget Guide",
      description: "Learn the simple budgeting rule: 50% needs, 30% wants, 20% savings",
      type: "Educational Guide",
      icon: <PiggyBank className="w-6 h-6" />,
      color: "#87A96B",
      action: "Learn Method",
      link: "https://www.nerdwallet.com/article/finance/nerdwallet-budget-calculator"
    }
  ];

  const assistanceResources = [
    {
      title: "Benefits Data Trust",
      description: "Help applying for SNAP, Medicaid, LIHEAP, and other public benefits — no cost",
      type: "No-Cost Assistance",
      icon: <Users className="w-6 h-6" />,
      color: "#3F5D4A",
      phone: "844-848-4376",
      link: "https://www.bdtrust.org"
    },
    {
      title: "LIHEAP (Heating Assistance)",
      description: "Low Income Home Energy Assistance Program helps pay heating and cooling bills",
      type: "Government Program",
      icon: <DollarSign className="w-6 h-6" />,
      color: "#87A96B",
      link: "https://www.dhs.pa.gov/Services/Assistance/Pages/LIHEAP.aspx"
    },
    {
      title: "PA COMPASS Benefits Portal",
      description: "Apply online for food stamps (SNAP), cash assistance, healthcare, and more",
      type: "Government Portal",
      icon: <Shield className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "Apply Online",
      link: "https://www.compass.state.pa.us"
    },
    {
      title: "Clarifi Financial Counseling",
      description: "Financial coaching, debt management, and housing counseling — at no cost",
      type: "Nonprofit Service",
      icon: <Users className="w-6 h-6" />,
      color: "#87A96B",
      phone: "215-563-5665",
      link: "https://clarifi.org"
    }
  ];

  const savingsResources = [
    {
      title: "America Saves",
      description: "Free program to help you set savings goals and stay motivated",
      type: "No-Cost Program",
      icon: <Target className="w-6 h-6" />,
      color: "#87A96B",
      action: "Take the Pledge",
      link: "https://americasaves.org"
    },
    {
      title: "SaverLife",
      description: "Save money and earn rewards - connect your account and get cash rewards for saving",
      type: "Rewards Program",
      icon: <PiggyBank className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "Start Earning",
      link: "https://www.saverlife.org"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8F4ED]">
      <Header />
      <div className="py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          
          {/* Back Navigation */}
          <div className="mb-8">
            <Link to="/resources" className="inline-flex items-center text-gray-600 hover:text-[#3F5D4A] transition-colors duration-200">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resource Center
            </Link>
          </div>

          {/* Page Title */}
          <div className="text-center space-y-6 mb-12">
            <div 
              className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-semibold"
              style={{backgroundColor: '#3F5D4A'}}
            >
              ESSENTIAL TOOLS
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800">
              Financial <span style={{color: '#3F5D4A'}}>Resources</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Tools and guides to help you build credit, create a budget, save money, and access benefits you may qualify for.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-3xl font-bold mb-1" style={{color: '#3F5D4A'}}>1×/Yr</div>
            <p className="text-gray-600 text-sm">Credit Reports Yearly (No Cost)</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-3xl font-bold mb-1" style={{color: '#87A96B'}}>30%</div>
            <p className="text-gray-600 text-sm">Max Rent of Income</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-3xl font-bold mb-1" style={{color: '#3F5D4A'}}>20%</div>
            <p className="text-gray-600 text-sm">Savings Goal</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-3xl font-bold mb-1" style={{color: '#87A96B'}}>670+</div>
            <p className="text-gray-600 text-sm">Good Credit Score</p>
          </div>
        </div>

        {/* Credit Building Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <CreditCard className="w-6 h-6 mr-3" style={{color: '#3F5D4A'}} />
            Credit Building & Monitoring
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {creditResources.map((resource, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{backgroundColor: resource.color + '20'}}
                    >
                      {React.cloneElement(resource.icon, { style: { color: resource.color } })}
                    </div>
                    <div className="flex-1">
                      <span 
                        className="px-2 py-1 rounded text-xs font-semibold mb-2 inline-block"
                        style={{backgroundColor: resource.color + '20', color: resource.color}}
                      >
                        {resource.type}
                      </span>
                      <h3 className="font-bold text-gray-800 text-lg mb-2">{resource.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      <a 
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 text-white rounded-lg text-sm font-semibold transition-colors"
                        style={{backgroundColor: resource.color}}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {resource.action}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Budgeting Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <Calculator className="w-6 h-6 mr-3" style={{color: '#87A96B'}} />
            Budgeting Tools & Guides
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {budgetResources.map((resource, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{backgroundColor: resource.color + '20'}}
                    >
                      {React.cloneElement(resource.icon, { style: { color: resource.color } })}
                    </div>
                    <div className="flex-1">
                      <span 
                        className="px-2 py-1 rounded text-xs font-semibold mb-2 inline-block"
                        style={{backgroundColor: resource.color + '20', color: resource.color}}
                      >
                        {resource.type}
                      </span>
                      <h3 className="font-bold text-gray-800 text-lg mb-2">{resource.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      {resource.internalLink ? (
                        <Link to={resource.internalLink}>
                          <Button
                            className="inline-flex items-center px-4 py-2 text-white rounded-lg text-sm font-semibold transition-colors"
                            style={{backgroundColor: resource.color}}
                          >
                            <Calculator className="w-4 h-4 mr-2" />
                            {resource.action}
                          </Button>
                        </Link>
                      ) : (
                        <a 
                          href={resource.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 text-white rounded-lg text-sm font-semibold transition-colors"
                          style={{backgroundColor: resource.color}}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          {resource.action}
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Financial Assistance Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <DollarSign className="w-6 h-6 mr-3" style={{color: '#3F5D4A'}} />
            Financial Assistance & Counseling
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {assistanceResources.map((resource, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{backgroundColor: resource.color + '20'}}
                    >
                      {React.cloneElement(resource.icon, { style: { color: resource.color } })}
                    </div>
                    <div className="flex-1">
                      <span 
                        className="px-2 py-1 rounded text-xs font-semibold mb-2 inline-block"
                        style={{backgroundColor: resource.color + '20', color: resource.color}}
                      >
                        {resource.type}
                      </span>
                      <h3 className="font-bold text-gray-800 text-lg mb-2">{resource.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {resource.phone && (
                          <a 
                            href={`tel:${resource.phone}`}
                            className="inline-flex items-center px-3 py-2 text-white rounded-lg text-sm font-semibold transition-colors"
                            style={{backgroundColor: resource.color}}
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            {resource.phone}
                          </a>
                        )}
                        <a 
                          href={resource.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          {resource.action || 'Website'}
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Savings Programs Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <PiggyBank className="w-6 h-6 mr-3" style={{color: '#87A96B'}} />
            Savings Programs & Incentives
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {savingsResources.map((resource, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{backgroundColor: resource.color + '20'}}
                    >
                      {React.cloneElement(resource.icon, { style: { color: resource.color } })}
                    </div>
                    <div className="flex-1">
                      <span 
                        className="px-2 py-1 rounded text-xs font-semibold mb-2 inline-block"
                        style={{backgroundColor: resource.color + '20', color: resource.color}}
                      >
                        {resource.type}
                      </span>
                      <h3 className="font-bold text-gray-800 text-lg mb-2">{resource.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      <a 
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 text-white rounded-lg text-sm font-semibold transition-colors"
                        style={{backgroundColor: resource.color}}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {resource.action}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Financial Wellness Tips</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{backgroundColor: '#3F5D4A20'}}>
                <Target className="w-6 h-6" style={{color: '#3F5D4A'}} />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Start Small</h4>
              <p className="text-gray-600 text-sm">Even $10/week adds up to $520/year. The key is consistency, not amount.</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{backgroundColor: '#87A96B20'}}>
                <CreditCard className="w-6 h-6" style={{color: '#87A96B'}} />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Pay Bills On Time</h4>
              <p className="text-gray-600 text-sm">Payment history is 35% of your credit score. Set up autopay or reminders.</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{backgroundColor: '#3F5D4A20'}}>
                <Shield className="w-6 h-6" style={{color: '#3F5D4A'}} />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Build Emergency Fund</h4>
              <p className="text-gray-600 text-sm">Aim for $500-$1,000 first, then work toward 3 months of expenses.</p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-[#F0EBE0] border border-[#D4C9B0] rounded-lg p-6 mb-12">
          <h3 className="font-semibold text-[#3F5D4A] mb-2">Important Note</h3>
          <p className="text-[#5C5852] text-sm">
            These resources are provided for informational purposes only. Always verify current program 
            requirements and availability. For personalized financial advice, consider speaking with a 
            certified financial counselor or HUD-approved housing counselor.
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div 
            className="rounded-2xl p-8 md:p-12 text-white"
            style={{background: '#87A96B'}}
          >
            <h3 className="text-3xl font-bold mb-4">Ready to Take the Next Step?</h3>
            <p className="text-xl mb-6 opacity-90">
              Building financial stability is a journey. We're here to support you every step of the way.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/programs">
                <Button 
                  className="px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg"
                  style={{
                    background: '#3F5D4A',
                    borderColor: '#3F5D4A'
                  }}
                >
                  Join Our Program
                </Button>
              </Link>
              <Link to="/">
                <Button 
                  variant="outline"
                  className="border-2 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200"
                  style={{
                    borderColor: '#FEFEFE',
                    color: '#FEFEFE',
                    backgroundColor: 'transparent'
                  }}
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default FinancialResources;
